import { VoiceRecorder, TranscriptionClient } from '@edge/edge-ai'
import { collectSttHints } from './stt-hints'

/**
 * Small interface over mic capture + streaming transcription so the voice
 * orchestration is unit-testable with a fake (per the frontend "design for
 * testability" doctrine). The real implementation reuses the exact
 * `VoiceRecorder` + `TranscriptionClient` plumbing the notebook's VoicePTT
 * validated (interim fallback included).
 */
export interface DictationResult {
  text: string
  /** Language the STT reported; undefined when it reported none. */
  language?: string
  /** STT confidence 0-1; undefined when the provider sends none. */
  confidence?: number
}

export interface DictationService {
  /** Start capturing; interim transcripts and mic levels stream out. */
  start(onInterim: (text: string) => void, onLevel: (level: number) => void): Promise<void>
  /** Stop and resolve the final transcript ('' when nothing was heard). */
  stop(): Promise<string>
  /** Like stop(), plus the language/confidence the transcript gate needs. */
  stopDetailed?(): Promise<DictationResult>
  /** Abandon capture without a result. */
  cancel(): void
}

export function createDictationService(
  apiBase: string,
  getAuthToken?: () => Promise<string>,
): DictationService {
  let recorder: VoiceRecorder | null = null
  let client: TranscriptionClient | null = null
  // Latest interim text — the fallback when stop() returns empty (same
  // behavior VoicePTT ships).
  let latestInterim = ''

  async function stopDetailed(): Promise<DictationResult> {
    recorder?.stop()
    recorder = null
    const c = client
    client = null
    if (!c) return { text: '' }
    const result = await c.stop()
    return {
      // The client joins trailing partial words itself; the last interim only
      // rescues a result that came back shorter than what was on screen.
      text: (latestInterim.length > result.text.length ? latestInterim : result.text).trim(),
      language: result.detectedLanguage,
      confidence: result.confidence,
    }
  }

  return {
    async start(onInterim, onLevel) {
      latestInterim = ''
      try {
        client = new TranscriptionClient(apiBase, getAuthToken, { getKeyterms: collectSttHints })
        await client.connect((text) => {
          latestInterim = text
          onInterim(text)
        })
        recorder = new VoiceRecorder()
        await recorder.start(
          (chunk) => client?.sendAudio(chunk),
          (level) => onLevel(level),
        )
      } catch (err) {
        // Mic denied / WS connect failed — tear down any partial connection.
        this.cancel()
        throw err
      }
    },

    async stop() {
      return (await stopDetailed()).text
    },

    stopDetailed,

    cancel() {
      recorder?.stop()
      recorder = null
      client?.disconnect()
      client = null
    },
  }
}
